<template>
    <transition name="fade">
        <div v-if="showModal" class="newsletter-modal">
            <div @click="closeModal" class="modal-backdrop"></div>
            <div class="modal-content">
                <div class="modal-header">
                    <div class="text-right">
                        <i @click="closeModal" class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
                    </div>
                </div>
                <div class="modal-body relative">
                    <InnerLoader v-if="isLoading" />
                    <img class="newsletter-img" :src="require('@/assets/newsletter.svg')" alt="" />
                    <h2 class="font-size-20 md:font-size-30 shadow-text-none text-center mt-5 mb-2">
                        {{ translate('newsletter_modal_header') }}
                    </h2>
                    <p class="text-center font-size-14 md:font-size-16 shadow-text-none mx-auto">
                        {{ translate('newsletter_modal_description') }}
                    </p>
                    <div class="mt-4 md:mt-8">
                        <form @submit.prevent="updateNewsletterEmail" class="email-box flex mx-auto" method="post">
                            <div>
                                <XInput
                                    v-validate="'required|email'"
                                    @change="e => (emailInput = e.target.value)"
                                    :value="emailInput"
                                    name="email"
                                ></XInput>
                            </div>
                            <XButton class="system-button-color system-button-text-color">
                                <span class="shadow-text-none">{{
                                    isSubscribed
                                        ? translate('newsletter_modal_update_email')
                                        : translate('newsletter_modal_button_send')
                                }}</span>
                            </XButton>
                        </form>
                        <div class="invalid-feedback">
                            <span v-if="errors.has('email')" class="has-error text-center shadow-text-none">
                                {{ errors.first('email') }}
                            </span>
                        </div>

                        <div v-if="isSubscribed" class="text-center mt-3">
                            <a
                                @click.prevent="unsubscribe"
                                class="shadow-text-none no-underline text-black text-sm"
                                href=""
                            >
                                <i class="far fa-bell-slash"></i>
                                {{ translate('newsletter_modal_unsubscribe') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import XInput from '@/components/v2/XInput'
import XButton from '@/components/v2/XButton'
import UserService from '@/lib/user.js'
import appConfig from '@/app.config.js'
import Request from '@/lib/request.js'
import helperMixins from '@/mixins/helper.js'
import popup from '@/lib/popup.js'
import InnerLoader from '@/components/InnerLoader'

export default {
    components: {
        XInput,
        XButton,
        InnerLoader
    },

    mixins: [helperMixins],

    data() {
        return {
            emailInput: '',
            showModal: false,
            isSubscribed: false,
            isLoading: false
        }
    },

    async mounted() {
        const userService = new UserService()

        if (!userService.getUser()) {
            this.showModal = true
            return
        }

        const getNewsletterData = await this.getNewsletterData()

        if (getNewsletterData && typeof getNewsletterData != 'undefined') {
            if (!getNewsletterData.is_prompt_newsletter || !getNewsletterData.receive_newsletter_email) {
                this.setIsPromptNewsletter()
            } else {
                this.setEmailInput(getNewsletterData.receive_newsletter_email)
            }

            this.isSubscribed = getNewsletterData.send_me_news
        } else {
            this.setIsPromptNewsletter()
        }

        this.showModal = true
    },

    methods: {
        openModal() {
            this.showModal = true
        },

        closeModal() {
            if (this.isLoading) {
                return
            }

            this.$emit('close')
        },

        setEmailInput(val) {
            this.emailInput = val
        },

        authRequest() {
            const userService = new UserService()
            const token = userService.getToken()

            return {
                Authorization: `Bearer ${token}`
            }
        },

        setIsPromptNewsletter() {
            const userService = new UserService()

            this.showModal = true

            this.$nextTick(() => {
                if (
                    ['teacher', 'teacher_plus', 'student', 'student_plus'].includes(userService.getUser().role) &&
                    !this.isSubscribed
                ) {
                    const email = userService.getUser().email.split('@')

                    if (email[0] === userService.getUser().username) {
                        this.setEmailInput('')
                    } else {
                        this.setEmailInput(userService.getUser().email)
                    }
                } else {
                    this.setEmailInput(userService.getUser().email)
                }

                const request = new Request({
                    headers: {
                        'Content-Type': 'application/json',
                        ...this.authRequest()
                    }
                })
                request.patch(appConfig.api.updateShowNewsletter)
            })
        },

        async getNewsletterData() {
            const request = new Request({
                headers: {
                    ...this.authRequest()
                }
            })

            let getEmail = await request.post(appConfig.api.getNewsletterPopupStatus)
            if (getEmail.status === 200 && getEmail.data) {
                return getEmail.data.data
            }
        },

        updateNewsletterEmail() {
            this.$validator.validate().then(result => {
                if (!result) {
                    return false
                }

                const userService = new UserService()

                const request = new Request({
                    headers: {
                        'Content-Type': 'application/json',
                        ...this.authRequest()
                    }
                })

                this.isLoading = true

                if (userService.isLoggedIn()) {
                    request
                        .patch(appConfig.api.updateNewsletterEmail, {
                            email: this.emailInput
                        })
                        .then(() => {
                            this.isLoading = false
                            this.closeModal()
                        })
                        .catch(() => {
                            this.isLoading = false
                            popup.alert(this.translate('newsletter_email_invalid'))
                        })
                } else {
                    request
                        .post(appConfig.api.createUserGuests, {
                            email: this.emailInput
                        })
                        .then(() => {
                            this.isLoading = false
                            this.closeModal()
                        })
                        .catch(() => {
                            this.isLoading = false
                            popup.alert(this.translate('newsletter_email_invalid'))
                        })
                }
            })
        },

        unsubscribe() {
            popup.confirm(this.translate('newsletter_modal_unsubscribe_message'), () => {
                const request = new Request({
                    headers: {
                        'Content-Type': 'application/json',
                        ...this.authRequest()
                    }
                })

                this.isLoading = true
                request
                    .patch(appConfig.api.unsubscribeNewsletter)
                    .then(() => {
                        this.isLoading = false
                        this.closeModal()
                    })
                    .catch(() => {
                        this.isLoading = false
                        this.closeModal()
                    })
            })
        }
    }
}
</script>

<style lang="scss">
.newsletter-modal {
    width: 100%;
    height: 100%;
    padding: 1rem 0.5rem;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    display: flex;
    justify-content: center;
    padding-top: 30px;

    .modal-backdrop {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
    }

    .modal-content {
        margin: 0 !important;
        background-color: #fff;
        // margin-top: 4rem;
        padding: 1rem 1rem 1.5rem 1rem;
        max-width: 720px;
        width: 100%;
        height: fit-content;
        overflow: unset !important;
        border-radius: 3px;
        box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);
        z-index: 2;

        @media screen and (max-width: 719px) {
            min-width: 90%;
            margin: 1rem 0.5rem;

            &.mb-content {
                max-height: 80%;
                height: 100%;
            }
        }

        .modal-body {
            height: auto !important;
            margin: 0 !important;

            @media screen and (max-width: 767px) {
                padding-bottom: 8px !important;
            }

            p {
                width: 85%;

                @media screen and (max-width: 767px) {
                    width: 100%;
                }
            }

            .newsletter-img {
                max-width: 250px;
                width: 100%;
                height: auto;
                display: block;
                margin: auto;

                @media screen and (max-width: 767px) {
                    width: 80%;
                }
            }

            .email-box {
                max-width: 400px;
                width: 100%;
                height: 50px;

                @media screen and (max-width: 767px) {
                    flex-direction: column;
                    row-gap: 16px;
                    height: auto;
                }

                input {
                    border: 1px solid #cbcbcb;
                    border-right: none;
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                    font-size: 16px;
                    width: 100%;
                    height: 100%;
                    padding: 0.8rem;

                    @media screen and (max-width: 767px) {
                        border-right: 1px solid #cbcbcb;
                        border-top-right-radius: 4px;
                        border-bottom-right-radius: 4px;
                    }
                }

                button {
                    border: none;
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    background-color: #dc3545;
                    width: 180px;
                    height: 100%;

                    @media screen and (max-width: 767px) {
                        border-top-left-radius: 4px;
                        border-bottom-left-radius: 4px;
                        width: 100%;
                        height: 46px;
                    }

                    span {
                        font-size: 16px;
                    }
                }
            }
        }
    }

    .invalid-feedback {
        color: #ef0000;
        font-size: 0.7rem;
        text-align: center;
        margin-top: 0.4rem;
    }
}
</style>
